<script setup lang="ts">
import type { NuxtError } from '#app';

import { Button } from '~ds/components/ui/button';
import { Heading } from '~ds/components/ui/typography';

import { useConfigStore } from '~/stores/config';
import { APP_TYPE } from '~/types/appConfig';

interface Props {
  error: NuxtError;
}
const props = defineProps<Props>();

const { isLoggedIn } = useAuth();

const configStore = useConfigStore();

const platformAppConfig = usePlatformAppConfig(useRoute().path);

const layout = computed(() => {
  // The app layouts need the bootstrap config (cluster registry, nav, i18n).
  // If it never loaded — e.g. this host's cluster is dark behind its release
  // flag, or the config API is down — render the error bare rather than
  // crashing inside a layout that has nothing to work with.
  if (!configStore.publicConfig) {
    return 'div';
  }

  // Switch on app type
  switch (platformAppConfig.appType) {
    case APP_TYPE.CONTENT:
      return resolveComponent('LayoutAdmin');
    case APP_TYPE.SCHOOLS:
      return resolveComponent('LayoutSchools');
    case APP_TYPE.UNIFIED:
      return resolveComponent('NuxtLayout');
    case APP_TYPE.ROOT:
      return 'div';
    default:
      return 'div';
  }
});

const layoutProps = computed(() =>
  platformAppConfig.appType === APP_TYPE.UNIFIED ? { name: 'unified-public' } : {},
);

const exitErrorScreen = async () => {
  await clearError({ redirect: isLoggedIn.value ? clusterRedirectPath(platformAppConfig.dashboardRedirectRoute) : clusterRedirectPath(platformAppConfig.onErrorRedirectRoute) });
};

const additionalContextMessage = computed<string | undefined>(() => {
  if (typeof props.error.data === 'string') {
    try {
      const parsedData = JSON.parse(props.error.data);
      return parsedData?.additionalContextMessage || undefined;
    }
    // eslint-disable-next-line unused-imports/no-unused-vars
    catch (_) {
      //
    }
  }
  return undefined;
});
</script>

<template>
  <component
    :is="layout"
    v-bind="layoutProps"
    data-theme="anyway"
  >
    <KUICoreContainerCentered>
      <div class="flex flex-col items-center justify-center" :data-testid="`error-header-${error.statusCode}`">
        <Heading
          class="mb-5"
          size="2xl"
          as="h3"
        >
          {{ error.message }}
        </Heading>
        <Heading
          v-if="additionalContextMessage"
          class="mb-5 mt-3"
          size="xl"
          as="h6"
        >
          {{ additionalContextMessage }}
        </Heading>
        <!-- Without config there is no i18n for the label and no working home route. -->
        <Button
          v-if="configStore.publicConfig"
          variant="outline"
          size="sm"
          type="button"
          @click="exitErrorScreen"
        >
          {{ isLoggedIn ? $t('general.nav.dashboard') : $t('general.nav.home') }}
        </Button>
      </div>
    </KUICoreContainerCentered>
  </component>
</template>
